import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 建筑设计 - 横屏「蓝图展开」风格
 *
 * 核心亮点：
 * - 蓝图网格背景 + 白色线稿
 * - 建筑线稿 SVG 描边动画
 * - 结构分层标注（地基/主体/屋顶）
 * - 尺寸标注箭头
 * - 材质/参数面板
 *
 * 尺寸：1920x1080 横屏
 */

interface StructureLayer {
  name: string;
  height: string;
  material: string;
  color: string;
  yStart: number;
  yEnd: number;
}

interface ArchParam {
  label: string;
  value: string;
  icon: string;
}

interface ArchitectureBlueprintTemplateProps {
  projectName: string;
  architect: string;
  style: string;
  layers: StructureLayer[];
  params: ArchParam[];
  designPrinciple: string;
  funFact: string;
}

const DEFAULT_PROPS: ArchitectureBlueprintTemplateProps = {
  projectName: '现代极简住宅设计',
  architect: '安藤忠雄风格',
  style: '现代主义 · 清水混凝土',
  layers: [
    { name: '屋顶', height: '3.2m', material: '钢结构 + 防水膜', color: '#60a5fa', yStart: 100, yEnd: 160 },
    { name: '二层', height: '3.0m', material: '清水混凝土', color: '#818cf8', yStart: 160, yEnd: 300 },
    { name: '一层', height: '3.5m', material: '清水混凝土 + 落地玻璃', color: '#a78bfa', yStart: 300, yEnd: 460 },
    { name: '地基', height: '1.5m', material: '钢筋混凝土基础', color: '#6b7280', yStart: 460, yEnd: 520 },
  ],
  params: [
    { label: '占地面积', value: '280 m²', icon: '📐' },
    { label: '建筑面积', value: '520 m²', icon: '🏗️' },
    { label: '层高', value: '2F + 屋顶', icon: '📏' },
    { label: '结构类型', value: '框架剪力墙', icon: '🧱' },
    { label: '抗震等级', value: '8 度设防', icon: '🛡️' },
    { label: '预算', value: '¥380 万', icon: '💰' },
  ],
  designPrinciple: '少即是多：通过光影与材质本身的质感创造空间诗意',
  funFact: '安藤忠雄没有建筑学学位，完全自学成才，却获得了建筑界最高荣誉普利兹克奖',
};

export const ArchitectureBlueprintTemplate: React.FC<Partial<ArchitectureBlueprintTemplateProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerProgress = spring({ frame, fps, config: { damping: 18, stiffness: 90 } });

  // Building draw
  const buildDraw = interpolate(frame, [30, 120], [0, 1], { extrapolateRight: 'clamp' });

  // Layers
  const layerStart = 80;
  const layerInterval = 40;

  // Params
  const paramStart = layerStart + p.layers.length * layerInterval + 20;
  const paramInterval = 18;

  // Design principle
  const dpStart = paramStart + p.params.length * paramInterval + 30;
  const dpOpacity = interpolate(frame, [dpStart, dpStart + 25], [0, 1], { extrapolateRight: 'clamp' });

  // Fun fact
  const ffStart = dpStart + 40;
  const ffOpacity = interpolate(frame, [ffStart, ffStart + 25], [0, 1], { extrapolateRight: 'clamp' });

  // Blueprint coordinate markers
  const gridSize = 30;

  return (
    <AbsoluteFill
      style={{
        background: '#1a2744',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Blueprint grid */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `linear-gradient(rgba(96,165,250,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(96,165,250,0.06) 1px, transparent 1px)`,
          backgroundSize: `${gridSize}px ${gridSize}px`,
        }}
      />
      {/* Thicker grid every 5 */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `linear-gradient(rgba(96,165,250,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(96,165,250,0.12) 1px, transparent 1px)`,
          backgroundSize: `${gridSize * 5}px ${gridSize * 5}px`,
        }}
      />

      {/* Header */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 40,
          opacity: headerProgress,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <div style={{ padding: '4px 14px', background: '#60a5fa18', border: '1px solid #60a5fa40', borderRadius: 8, fontSize: 22, color: '#60a5fa', fontWeight: 600 }}>
            🏛️ ARCHITECTURE
          </div>
          <span style={{ fontSize: 22, color: 'rgba(255,255,255,0.25)' }}>{p.style}</span>
        </div>
        <h1 style={{ fontSize: 36, fontWeight: 800, margin: 0 }}>{p.projectName}</h1>
        <p style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>{p.architect}</p>
      </div>

      {/* ========== BUILDING ELEVATION SVG ========== */}
      <svg
        style={{ position: 'absolute', left: 40, top: 130 }}
        width="780"
        height="600"
        viewBox="0 0 780 600"
      >
        {/* Building outline */}
        <path
          d={`
            M100,520 L100,160
            L120,140 L380,100 L640,140 L660,160
            L660,520
            Z
          `}
          fill="none"
          stroke="#60a5fa"
          strokeWidth="2"
          strokeDasharray="2000"
          strokeDashoffset={2000 * (1 - buildDraw)}
          opacity="0.8"
        />

        {/* Windows */}
        {[[180, 200, 80, 70], [340, 200, 80, 70], [500, 200, 80, 70],
          [180, 340, 80, 100], [340, 340, 80, 100], [500, 340, 80, 100]].map(([x, y, w, h], i) => {
          const winProgress = interpolate(buildDraw, [0.4 + i * 0.05, 0.5 + i * 0.05], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
          return (
            <rect
              key={i}
              x={x}
              y={y}
              width={w}
              height={h}
              fill="rgba(96,165,250,0.04)"
              stroke="#60a5fa"
              strokeWidth="1.5"
              opacity={winProgress * 0.7}
              rx="2"
            />
          );
        })}

        {/* Door */}
        <rect
          x="340"
          y="420"
          width="80"
          height="100"
          fill="rgba(96,165,250,0.06)"
          stroke="#60a5fa"
          strokeWidth="1.5"
          opacity={buildDraw > 0.6 ? 0.7 : 0}
          rx="2"
        />

        {/* Ground line */}
        <line x1="50" y1="520" x2="720" y2="520" stroke="#60a5fa" strokeWidth="1" opacity="0.3" strokeDasharray="6 4" />

        {/* Layer brackets + labels */}
        {p.layers.map((layer, i) => {
          const start = layerStart + i * layerInterval;
          const opacity = interpolate(frame, [start, start + 20], [0, 1], { extrapolateRight: 'clamp' });
          const midY = (layer.yStart + layer.yEnd) / 2;

          return (
            <g key={i} opacity={opacity}>
              {/* Right bracket */}
              <line x1="690" y1={layer.yStart} x2="710" y2={layer.yStart} stroke={layer.color} strokeWidth="1.5" />
              <line x1="710" y1={layer.yStart} x2="710" y2={layer.yEnd} stroke={layer.color} strokeWidth="1.5" />
              <line x1="690" y1={layer.yEnd} x2="710" y2={layer.yEnd} stroke={layer.color} strokeWidth="1.5" />

              {/* Layer name */}
              <text x="725" y={midY - 4} fill={layer.color} fontSize="22" fontWeight="bold">{layer.name}</text>
              <text x="725" y={midY + 24} fill="rgba(255,255,255,0.35)" fontSize="22">{layer.height}</text>

              {/* Horizontal fill hint */}
              <rect x="100" y={layer.yStart} width={560 * buildDraw} height={layer.yEnd - layer.yStart} fill={layer.color} opacity="0.03" />
            </g>
          );
        })}

        {/* Dimension line at bottom */}
        {buildDraw > 0.8 && (
          <g opacity={buildDraw - 0.8}>
            <line x1="100" y1="545" x2="660" y2="545" stroke="#60a5fa" strokeWidth="1" opacity="0.4" />
            <line x1="100" y1="540" x2="100" y2="550" stroke="#60a5fa" strokeWidth="1" opacity="0.4" />
            <line x1="660" y1="540" x2="660" y2="550" stroke="#60a5fa" strokeWidth="1" opacity="0.4" />
            <text x="380" y="562" fill="#60a5fa" fontSize="13" textAnchor="middle" opacity="0.5">16.8m</text>
          </g>
        )}
      </svg>

      {/* ========== RIGHT PANEL: Params ========== */}
      <div
        style={{
          position: 'absolute',
          top: 170,
          right: 240,
          width: 400,
        }}
      >
        <div style={{ fontSize: 28, color: 'rgba(255,255,255,0.25)', letterSpacing: '0.1em', marginBottom: 14 }}>建筑参数</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {p.params.map((param, i) => {
            const start = paramStart + i * paramInterval;
            const opacity = interpolate(frame, [start, start + 15], [0, 1], { extrapolateRight: 'clamp' });
            return (
              <div
                key={i}
                style={{
                  padding: '14px 16px',
                  background: 'rgba(96,165,250,0.04)',
                  borderRadius: 12,
                  border: '1px solid rgba(96,165,250,0.1)',
                  opacity,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <span style={{ fontSize: 20 }}>{param.icon}</span>
                  <span style={{ fontSize: 24, color: 'rgba(255,255,255,0.35)' }}>{param.label}</span>
                </div>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#60a5fa' }}>{param.value}</div>
              </div>
            );
          })}
        </div>

        {/* Material legend */}
        <div style={{ marginTop: 20, fontSize: 28, color: 'rgba(255,255,255,0.25)', marginBottom: 10 }}>材料标注</div>
        {p.layers.map((layer, i) => {
          const opacity = interpolate(frame, [layerStart + i * layerInterval, layerStart + i * layerInterval + 20], [0, 1], { extrapolateRight: 'clamp' });
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, opacity }}>
              <div style={{ width: 12, height: 12, borderRadius: 3, background: layer.color, border: `1px solid ${layer.color}50` }} />
              <span style={{ fontSize: 24, color: layer.color, fontWeight: 600, width: 50 }}>{layer.name}</span>
              <span style={{ fontSize: 24, color: 'rgba(255,255,255,0.4)' }}>{layer.material}</span>
            </div>
          );
        })}
      </div>

      {/* ========== BOTTOM ========== */}
      <div
        style={{
          position: 'absolute',
          bottom: 50,
          left: 40,
          right: 40,
          display: 'flex',
          gap: 20,
        }}
      >
        <div style={{ flex: 1, opacity: dpOpacity }}>
          <div
            style={{
              padding: '16px 20px',
              background: 'rgba(96,165,250,0.05)',
              border: '1px solid rgba(96,165,250,0.15)',
              borderRadius: 14,
            }}
          >
            <div style={{ fontSize: 26, color: '#60a5fa', fontWeight: 600, marginBottom: 6 }}>✏️ 设计理念</div>
            <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.75)', lineHeight: 1.5 }}>{p.designPrinciple}</div>
          </div>
        </div>
        <div style={{ flex: 1, opacity: ffOpacity }}>
          <div
            style={{
              padding: '16px 20px',
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.05)',
              borderRadius: 14,
            }}
          >
            <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)', fontWeight: 600, marginBottom: 6 }}>💡 冷知识</div>
            <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>{p.funFact}</div>
          </div>
        </div>
      </div>

      {/* Compass rose */}
      <div
        style={{
          position: 'absolute',
          bottom: 180,
          left: 60,
          opacity: buildDraw,
        }}
      >
        <svg width="50" height="50" viewBox="0 0 50 50">
          <circle cx="25" cy="25" r="20" fill="none" stroke="#60a5fa" strokeWidth="1" opacity="0.2" />
          <line x1="25" y1="8" x2="25" y2="42" stroke="#60a5fa" strokeWidth="1" opacity="0.3" />
          <line x1="8" y1="25" x2="42" y2="25" stroke="#60a5fa" strokeWidth="1" opacity="0.3" />
          <text x="25" y="7" fill="#60a5fa" fontSize="10" textAnchor="middle" fontWeight="bold" opacity="0.5">N</text>
        </svg>
      </div>

      {/* Progress */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 480], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 3,
          background: '#60a5fa',
        }}
      />
    </AbsoluteFill>
  );
};
